import Markdown from "react-markdown";
import type { HermesUpdate } from "../types";
import { utcTime, ago } from "../format";

const PHASE: Record<string, { label: string; cls: string }> = {
  learning: { label: "Learning", cls: "hp-learn" },
  developing: { label: "Developing", cls: "hp-dev" },
  backtesting: { label: "Backtesting", cls: "hp-test" },
  promoting: { label: "Promoting", cls: "hp-promote" },
  review: { label: "Review", cls: "hp-review" },
  note: { label: "Note", cls: "hp-note" },
};

function Metrics({ m }: { m?: Record<string, number> | null }) {
  if (!m) return null;
  const keys = Object.keys(m);
  if (!keys.length) return null;
  return (
    <ul className="hermes-metrics">
      {keys.map((k) => (
        <li key={k}>
          <span className="hm-k">{k}</span>
          <span className="hm-v">{typeof m[k] === "number" ? m[k].toFixed(2) : String(m[k])}</span>
        </li>
      ))}
    </ul>
  );
}

function freshness(ts: string): { cls: string; text: string } {
  const secs = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 180) return { cls: "live", text: "active now" };
  if (secs < 3600) return { cls: "recent", text: `${Math.round(secs / 60)} min ago` };
  if (secs < 86400) return { cls: "idle", text: `${Math.round(secs / 3600)}h ago` };
  return { cls: "idle", text: `${Math.round(secs / 86400)}d ago` };
}

export default function HermesUpdates({ updates }: { updates: HermesUpdate[] }) {
  if (!updates.length) {
    return (
      <p className="muted">
        Hermes hasn't posted yet. The research agent runs continuously in its Docker container —
        its learning notes, the strategies it is developing, backtests and decisions will stream here.
      </p>
    );
  }
  const f = freshness(updates[0].ts);
  return (
    <>
      <div className={`hermes-live hermes-live-${f.cls}`} role="status" aria-live="polite">
        <span className="hermes-pulse" aria-hidden="true">●</span>
        <strong>{f.cls === "live" ? "Hermes is working" : "Hermes"}</strong>
        <span className="muted"> · last activity {f.text} · {updates.length} updates streamed</span>
      </div>
      <ol className="hermes-feed" aria-label="Hermes research updates">
      {updates.map((u) => {
        const p = PHASE[u.phase] ?? PHASE.note;
        return (
          <li key={u.id} className="hermes-card">
            <div className="hermes-head">
              <span className={`hermes-phase ${p.cls}`}>{p.label}</span>
              <h3>{u.title}</h3>
              <time dateTime={u.ts} title={u.ts} className="muted small">
                {utcTime(u.ts)} · {ago(u.ts)}
              </time>
            </div>
            {(u.instrument || u.strategy) && (
              <p className="hermes-tags muted small">
                {u.instrument && <span className="tag">{u.instrument}</span>}
                {u.strategy && <span className="tag">{u.strategy}</span>}
              </p>
            )}
            <div className="markdown hermes-body">
              <Markdown>{u.body_md}</Markdown>
            </div>
            <Metrics m={u.metrics} />
            {u.source && (
              <p className="hermes-source muted small">
                Source:{" "}
                {/^https?:\/\//.test(u.source) ? (
                  <a href={u.source} target="_blank" rel="noreferrer noopener">{u.source}</a>
                ) : (
                  u.source
                )}
              </p>
            )}
          </li>
        );
      })}
      </ol>
    </>
  );
}
